import React, { useState, useRef, useMemo, useContext } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Bread from "../../common/Bread";
import { LangContext } from "../../context/LangContext";
import Loader from "../../common/Loader";
import "./investor.scss";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#003399",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "0.875rem",
    fontFamily: "DM Sans, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: "rgba(173, 216, 230, 0.4)",
  },
}));

const InvestorCK = () => {
  const items = useRef([]);
  const [title, setTitle] = useState("");
  const { slug } = useParams();
  const lang = useContext(LangContext);
  const nep = lang.state.np;

  // Map slug to title dynamically (supports Nepali easily later)
  const titleMap = {
    capital_structure: nep ? "पूँजी संरचना" : "Capital Structure",
    base_rates: nep ? "आधार दर" : "Base Rate",
    spread_rates: nep ? "ब्याज प्रसार दर" : "Interest Spread Rate",
    cost_of_fund: nep ? "कोषको लागत" : "Cost of Fund",
  };

  useMemo(() => {
    setTitle(
      titleMap[slug] || (nep ? "लगानीकर्तासँगको सम्बन्ध" : "Investor Relation"),
    );
  }, [slug, nep]);

  const { isLoading, isFetching, error, data } = useQuery({
    queryKey: ["investorCKData", slug],
    queryFn: () =>
      axios
        .get(`${process.env.REACT_APP_BASE_URL}/invest_interest`)
        .then((res) => res.data),
  });

  if (isFetching || isLoading) return <Loader />;
  if (error) return "An error has occurred: " + error.message;

  // Breadcrumb
  const breadcrumList = {
    image: "",
    title: title,
    l1: nep ? "होम" : "Home",
    l2: nep ? "लगानीकर्तासँगको सम्बन्ध" : "Investor Relation",
    l3: "",
  };

  // Utility to render table dynamically
  const renderTable = (dataArray, rateTitle) => (
    <TableContainer tabIndex={0} role="region" aria-label={rateTitle} sx={{ border: "1px solid #c2c2c2" }} component={Paper}>
      <Table sx={{ minWidth: 600 }} aria-label={`${rateTitle} table`}>
        <caption className="a11y-sr-only">{rateTitle}</caption>
        <TableHead>
          <TableRow>
            <StyledTableCell scope="col" sx={{ fontSize: "1.0625rem" }}>Month</StyledTableCell>
            <StyledTableCell scope="col" sx={{ fontSize: "1.0625rem" }}>
              {rateTitle}
            </StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {dataArray.map((item, index) => (
            <StyledTableRow key={index}>
              <StyledTableCell scope="row" sx={{ width: "70%" }} align="left">
                {item.Title}
              </StyledTableCell>
              <StyledTableCell sx={{ width: "30%" }} align="left">
                {Number(item.rate).toFixed(2)}%
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Bread data={breadcrumList}>
      <div className="investorContainer" tabIndex={0} role="region" aria-label={title}>
        {slug === "capital_structure" && (
          <div
            className="services-ck-content"
            dangerouslySetInnerHTML={{
              __html: data.capital_structure.en.Description,
            }}
          />
        )}
        {slug === "base_rates" && renderTable(data.base_rates.en, "Base Rate")}
        {slug === "spread_rates" &&
          renderTable(data.spread_rates.en, "Interest Spread Rate")}
        {slug === "cost_of_fund" &&
          renderTable(data.cost_of_fund.en, "Cost of Fund")}
        {![
          "capital_structure",
          "base_rates",
          "spread_rates",
          "cost_of_fund",
        ].includes(slug) && (
          <div className="ck-content">
            <h1>No data available</h1>
          </div>
        )}
      </div>
    </Bread>
  );
};

export default InvestorCK;
